/**
 * Public Guard
 *
 * For routes that are accessible by everyone (authenticated or not).
 * This is mainly for explicit marking and consistency.
 *
 * Optionally can show different content based on auth state.
 *
 * Usage:
 * ```tsx
 * // Simple - everyone can access
 * <PublicGuard>
 *   <LandingPage />
 * ./public-guard>
 *
 * // With auth-aware rendering
 * <PublicGuard
 *   renderForAuth={(user) => <AuthenticatedLanding user={user} />}
 * >
 *   <LandingPage />
 * ./public-guard>
 * ```
 */

import type { ReactNode } from "react"

import { useAuth } from "@/contexts/auth-context"

import { LoadingFallback } from "./loading-fallback"

import type { BaseGuardProps } from "./types"

interface PublicGuardProps extends BaseGuardProps {
  /** Optional: render different content for authenticated users */
  renderForAuth?: (user: { id: string; name: string; email: string | null }) => ReactNode
}

export function PublicGuard({
  children,
  fallback,
  renderForAuth,
}: PublicGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth()

  // Show loading state (optional for public routes)
  if (isLoading && fallback) {
    return <>{fallback}</>
  }

  // If authenticated and custom render provided, use it
  if (isAuthenticated && renderForAuth && user) {
    return <>{renderForAuth(user)}</>
  }

  // Render children for everyone
  return <>{children}</>
}
